import { z } from 'zod';
import { auStateCodeEnum, citextEmailSchema, requirementPatternEnum, complianceRequirementSchema, complianceRecordSchema, complianceRecordStatusEnum, documentSchema, evidenceSchema, hex64Schema, idSchema, inetSchema, isoDateSchema, isoDateTimeSchema, jobRunStatusEnum, nqaAddressTypeEnum, nqaNatureOfEmploymentEnum, nqaNonEducatorRoleEnum, nqaPositionEnum, personSchema, personStatusEnum, personTypeEnum, reminderSchema, } from './entities.js';
import { meAcknowledgementHistoryEntrySchema, meReminderHistoryEntrySchema, trainingChecklistRowSchema, } from './training-checklist.js';
/**
 * Wire-shape schemas for every endpoint listed in `PRD/04 §API surface`.
 *
 * Conventions:
 *   - Each endpoint has a request-body schema (or query-string schema for
 *     GETs that accept filters) and a response-body schema. Either may
 *     be omitted when the endpoint takes no body or returns no body
 *     (e.g. `POST /admin/logout` returns 204).
 *   - 2xx success shapes only. Error shapes are uniform across the API
 *     (`apiErrorResponseSchema` below) — every endpoint returns this
 *     shape on 4xx/5xx, so the typed client can dispatch error handling
 *     once.
 *   - Path parameters are not modelled here (they're part of the route);
 *     query-string schemas are modelled because the typed client needs
 *     to know how to serialise them.
 *   - Pagination follows a single shape (`paginationParamsSchema` /
 *     `paginatedResponseSchema(...)`) so list endpoints don't reinvent
 *     it; the API may evolve to cursor-based later without changing
 *     callers since the response shape is opaque to them.
 *   - Names follow `<verbNoun>RequestSchema` / `<verbNoun>ResponseSchema`,
 *     and the inferred TS types follow `<VerbNoun>Request` / `<VerbNoun>Response`.
 */
// ===========================================================================
// Shared sub-shapes
// ===========================================================================
/**
 * Uniform error response. The `code` is a stable string the typed client
 * can branch on (e.g. `token_already_used`, `name_mismatch_no_override`,
 * `permission_denied`); `message` is human-readable text safe to surface
 * to the user; `details` is an optional opaque object (e.g. zod issues)
 * useful for diagnosis but never load-bearing for client behaviour.
 *
 * `request_id` is always set so the user / admin can quote it when
 * raising a support request — the same value is on the server-side
 * `audit_events.request_id` row when the failure is itself audited.
 */
export const apiErrorResponseSchema = z.object({
    error: z.object({
        code: z.string().min(1),
        message: z.string().min(1),
        details: z.record(z.string(), z.unknown()).nullable(),
    }),
    request_id: z.string().min(1),
});
/** Standard pagination params — consumed by every list endpoint. */
export const paginationParamsSchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    page_size: z.coerce.number().int().positive().max(200).default(50),
});
/**
 * Query-string boolean parser. `z.coerce.boolean()` does JS-style truthiness
 * (`Boolean("false") === true`), so it can't be used here — the literal string
 * "false" arrives over the wire from the SPA and must mean `false`. Accepts
 * actual booleans (e.g. when the schema is reused inside JSON bodies) plus
 * the four common string forms.
 */
const querystringBool = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1');
/** Standard pagination envelope for list responses. */
export const paginationMetaSchema = z.object({
    page: z.number().int().positive(),
    page_size: z.number().int().positive(),
    total_items: z.number().int().nonnegative(),
    total_pages: z.number().int().nonnegative(),
});
/**
 * Higher-order helper for list responses. Used at definition sites like
 * `peopleListResponseSchema = paginatedResponseSchema(personSchema)`
 * so the envelope shape is identical across endpoints.
 */
export const paginatedResponseSchema = (item) => z.object({
    items: z.array(item),
    pagination: paginationMetaSchema,
});
// ===========================================================================
// Admin auth — passwordless email-OTP + mandatory TOTP
//
//   POST /admin/sign-in              { email }            → 200 generic
//   POST /admin/sign-in/verify-code  { email, code }      → 200 mfa_token
//                                                          | 200 enrolment
//   POST /admin/sign-in/totp         { mfa_token, code }  → sets cookie
//   POST /admin/totp/enrol-confirm   { enrol_token, code} → sets cookie
//   POST /admin/logout               (no body)            → 204
//   GET  /admin/me                                         → admin profile
//
// Admin auth is the same email-OTP shape volunteers see at `/sign-in`
// with a mandatory TOTP second factor layered on top. There is no
// password anywhere in the flow. First-time sign-in (or after a peer
// reset) lands on a forced TOTP-enrolment screen — every other admin
// endpoint short-circuits with 412 `totp_enrolment_required` until the
// new secret is confirmed.
// ===========================================================================
/**
 * Step 1 — admin enters their email at `/admin/sign-in`. The API issues
 * a 6-digit code (5-minute TTL) to the address if it matches an active
 * `admin_users.email` and returns a generic message either way. The
 * response shape is identical on hit / miss / rate-limit so the
 * endpoint cannot be used to enumerate admin emails.
 */
export const adminSignInRequestSchema = z.object({
    email: citextEmailSchema,
});
export const adminSignInResponseSchema = z.object({
    message: z.string().min(1),
});
/**
 * Step 2 — admin enters the 6-digit code from the email. On success the
 * server issues either an `mfa_token` (admin already has TOTP enrolled)
 * or an `enrol_token` (TOTP missing — forced-enrolment branch). The two
 * responses are distinguishable by the discriminator so the SPA can
 * route the next step accordingly. Failures collapse to a single
 * `invalid_code` for enumeration resistance.
 */
export const adminSignInVerifyCodeRequestSchema = z.object({
    email: citextEmailSchema,
    code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from the email.'),
});
const adminSignInVerifyTotpChallengeSchema = z.object({
    challenge: z.literal('totp'),
    mfa_token: z.string().min(1),
    expires_at: isoDateTimeSchema,
});
const adminSignInVerifyEnrolChallengeSchema = z.object({
    challenge: z.literal('totp_enrolment_required'),
    enrol_token: z.string().min(1),
    expires_at: isoDateTimeSchema,
    otpauth_uri: z.string().min(1),
    secret_base32: z.string().min(1),
});
export const adminSignInVerifyCodeResponseSchema = z.discriminatedUnion('challenge', [
    adminSignInVerifyTotpChallengeSchema,
    adminSignInVerifyEnrolChallengeSchema,
]);
/**
 * Step 3 — admin enters the TOTP code from their authenticator. The
 * `mfa_token` from step 2 + a fresh 6-digit TOTP code mints the session
 * cookie. Bad code / expired token / disabled account collapse to a
 * single `mfa_failed` error.
 */
export const adminSignInTotpRequestSchema = z.object({
    mfa_token: z.string().min(1),
    totp_code: z.string().regex(/^\d{6}$/, 'TOTP code must be 6 digits'),
});
/**
 * On success the API sets the admin session cookie and returns the admin
 * profile. The session itself is the cookie; this body lets the SPA
 * render the "Hi {name}" header without a follow-up request.
 */
export const adminSignInTotpResponseSchema = z.object({
    admin_user_id: idSchema,
    email: citextEmailSchema,
    full_name: z.string().min(1),
});
/**
 * Forced-enrolment confirm — verifies a TOTP code against the freshly
 * minted secret returned with the `enrol_token` in step 2 above. On
 * success the server persists `mfa_secret`, sets the admin session
 * cookie, and returns the same profile shape as the TOTP step. On
 * failure (`enrol_failed`) the SPA stays on the enrolment screen and
 * the admin can re-try the code from their authenticator app.
 */
export const adminTotpEnrolConfirmRequestSchema = z.object({
    enrol_token: z.string().min(1),
    totp_code: z.string().regex(/^\d{6}$/, 'TOTP code must be 6 digits'),
});
export const adminTotpEnrolConfirmResponseSchema = adminSignInTotpResponseSchema;
// `POST /admin/logout` — no body, 204 response.
/**
 * `GET /admin/me` — returns the currently-authenticated admin's profile so
 * the SPA can render the header without embedding the admin's id in a
 * cookie body.
 */
export const adminMeResponseSchema = adminSignInTotpResponseSchema;
// ===========================================================================
// Admin user CRUD — admin-only (every admin is a full admin post-M15)
// ===========================================================================
/**
 * Create a new admin user. The actor sets the email and full name —
 * there is no password (admins authenticate via email-OTP + TOTP). The
 * new admin signs in at `/admin/sign-in` with the email-code; their
 * first sign-in lands on the forced TOTP-enrolment screen because
 * `mfa_secret` is null at create time.
 */
export const createAdminUserRequestSchema = z.object({
    email: citextEmailSchema,
    full_name: z.string().min(1).max(200),
});
/**
 * Admin-user public projection — never emits `mfa_secret`.
 * `mfa_enrolled` is a derived boolean (`mfa_secret IS NOT NULL`) so the SPA
 * can render a "TOTP missing — will enrol on next sign-in" badge without
 * the server leaking the secret itself.
 */
export const adminUserPublicSchema = z.object({
    admin_user_id: idSchema,
    email: citextEmailSchema,
    full_name: z.string().min(1),
    is_active: z.boolean(),
    mfa_enrolled: z.boolean(),
    created_at: isoDateTimeSchema,
    last_login_at: isoDateTimeSchema.nullable(),
});
export const createAdminUserResponseSchema = adminUserPublicSchema;
export const adminUsersListResponseSchema = paginatedResponseSchema(adminUserPublicSchema);
/**
 * `GET /admin/users/:adminUserId` — single admin-user hydrate for the
 * admin-user detail / edit screen. Same public projection as the list
 * row. 404 `admin_not_found` when the id is stale.
 */
export const getAdminUserResponseSchema = adminUserPublicSchema;
/**
 * Partial update — any subset of fields may be changed. There is no
 * `password` field (admins have no passwords); TOTP rotation/reset
 * happens through the dedicated TOTP endpoints. `is_active=false`
 * deactivates the account (next sign-in fails with
 * `admin_account_disabled`).
 */
export const updateAdminUserRequestSchema = z
    .object({
    full_name: z.string().min(1).max(200).optional(),
    is_active: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided.',
});
export const updateAdminUserResponseSchema = adminUserPublicSchema;
// ===========================================================================
// Admin TOTP — self-rotate + peer-reset
// ===========================================================================
/**
 * `POST /admin/users/:id/totp/enrol` — self-rotate. The actor is
 * authenticated and presenting their existing session, so the route
 * mints a fresh TOTP secret on their own row and returns the
 * provisioning URI exactly once. The new secret is persisted as the
 * row's `mfa_secret` immediately. Scoped to the actor's own row only:
 * peer rotation goes through `/totp/reset` (with a step-up TOTP) so a
 * stolen session can't silently swap every peer's second factor.
 *
 * `otpauth_uri` is the full `otpauth://totp/…` URI; `secret_base32` is
 * the same secret in base32 for manual entry.
 */
export const adminTotpEnrolResponseSchema = z.object({
    otpauth_uri: z.string().min(1),
    secret_base32: z.string().min(1),
});
/**
 * `POST /admin/users/:id/totp/reset` — peer reset. A compliance admin
 * clears another admin's TOTP so the affected admin can re-enrol from
 * scratch on a new device. Requires a step-up TOTP code from the actor
 * in the same request — without it a stolen session could rotate every
 * peer's second factor without re-presenting its own. Self-reset is
 * rejected (would defeat the second factor); the only self-rotation
 * path is `/totp/enrol` from an authenticated session, which still
 * requires the actor to have signed in with the existing TOTP within
 * session lifetime.
 *
 * The audit row records both the actor's totp_jti and the affected
 * `admin_user_id` under `action='admin_user.totp_reset_by_peer'`.
 */
export const adminTotpResetRequestSchema = z.object({
    actor_totp_code: z.string().regex(/^\d{6}$/, 'Step-up TOTP code must be 6 digits'),
});
export const adminTotpResetResponseSchema = adminUserPublicSchema;
// ===========================================================================
// Volunteer auth (self-service) — two-step email + OTP sign-in
//   POST /volunteer/sign-in        — { email } → send 6-digit OTP
//   POST /volunteer/sign-in/verify — { email, code } → set session cookie
//   POST /volunteer/sign-out
// ===========================================================================
/**
 * Step 1 — volunteer submits their email at `/sign-in`. On a match the
 * API issues a 6-digit OTP and returns 200 with the code-sent message.
 * On no match the API returns 404 `email_not_registered` so the SPA can
 * tell the visitor to contact their school admin (deliberate departure
 * from PRD 04 §Self-service portal's enumeration-resistance — see
 * volunteer.ts).
 */
export const volunteerSignInRequestSchema = z.object({
    email: citextEmailSchema,
});
export const volunteerSignInResponseSchema = z.object({
    message: z.string().min(1),
});
/**
 * Step 2 — volunteer types the 6-digit code they received by email. On
 * success the API sets the `volunteer_sessions` cookie and returns the
 * minimal profile the SPA needs to render `/me`. All failures (bad code,
 * expired code, no matching email, disabled volunteer) collapse to a
 * single opaque `invalid_code` error so the verify step can't be used
 * to enumerate either.
 */
export const volunteerSignInVerifyRequestSchema = z.object({
    email: citextEmailSchema,
    code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from the email.'),
});
export const volunteerSignInVerifyResponseSchema = z.object({
    person_id: idSchema,
    full_name: z.string().min(1),
    preferred_name: z.string().nullable(),
});
// `POST /volunteer/sign-out` — no body, 204 response.
// ===========================================================================
// Volunteer self-service `/me` — GET /me, POST /me/evidence,
//                                POST /me/evidence/confirm
// ===========================================================================
/**
 * Volunteer-facing projection of `people` (PRD 06 §`/me` §1 Header).
 * Excludes admin-only / internal columns: `notes`, `deleted_at`,
 * `nqa_its_entry_id`, timestamps, etc. Anything here is safe for the
 * volunteer to see about themselves — and the editable subset is what
 * `PATCH /me/profile` writes back via `meProfileUpdateRequestSchema`
 * (PRD 04 §Volunteer self-service §`/me/profile`, M12).
 */
export const meSelfPersonSchema = z.object({
    person_id: idSchema,
    /** Projected from `concat_ws(' ', first_name, middle_name, last_name)`;
     *  the stored column was dropped in migration 0034. The DTO field name
     *  stays so the SPA can keep rendering it unchanged. */
    full_name: z.string().min(1),
    preferred_name: z.string().nullable(),
    contact_email: citextEmailSchema,
    person_type: personTypeEnum,
    role_tags: z.array(z.string()),
    status: personStatusEnum,
    /** Null until the admin sets it at create, the admin patches it
     *  later, or the volunteer confirms at intake (PRD 03 §1). */
    start_date: isoDateSchema.nullable(),
    end_date: isoDateSchema.nullable(),
    // Volunteer-editable contact + name + address fields (M12). Mirrors
    // the admin SPA's profile form, scoped to the subset the volunteer
    // can self-edit. Read on `GET /me`; written via `PATCH /me/profile`.
    title: z.string().nullable(),
    first_name: z.string().min(1),
    middle_name: z.string().nullable(),
    last_name: z.string().min(1),
    mobile_phone: z.string().nullable(),
    address_line_1: z.string().nullable(),
    address_line_2: z.string().nullable(),
    address_suburb: z.string().nullable(),
    address_state: auStateCodeEnum.nullable(),
    address_postcode: z.string().nullable(),
});
/**
 * `PATCH /me/profile` — volunteer self-edits their own contact + name
 * + address fields from the `/me` dashboard (M12, PRD 04 §Volunteer
 * self-service). Subset of `updatePersonRequestSchema`:
 *   - `contact_email` is **not** here — email change stays admin-only
 *     (preserves the M5 email-change-revokes-sessions invariant).
 *   - `date_of_birth` and `start_date` are **not** here — captured at
 *     intake verification and part of the legal record.
 *   - NQA-only admin fields (`nqa_position`, `non_educator_role`,
 *     `nature_of_employment`, `on_probationary_period`,
 *     `wwcc_exemption`/`wwcc_exemption_reason`,
 *     `no_qualification_training`, alias names) stay admin-only.
 *
 * NQA-relevant edits the volunteer can land (any name part,
 * mobile_phone, address) bump `people.updated_at` on the server side,
 * which surfaces the volunteer on the dashboard's "needs NQA ITS sync"
 * panel — same predicate (`people.updated_at > nqa_its_synced_at`) as
 * admin-driven edits per M3.
 */
export const meProfileUpdateRequestSchema = z
    .object({
    title: z.string().min(1).nullable().optional(),
    first_name: z.string().min(1).optional(),
    middle_name: z.string().min(1).nullable().optional(),
    last_name: z.string().min(1).optional(),
    preferred_name: z.string().min(1).nullable().optional(),
    mobile_phone: z.string().min(1).max(40).nullable().optional(),
    address_line_1: z.string().min(1).max(200).nullable().optional(),
    address_line_2: z.string().min(1).max(200).nullable().optional(),
    address_suburb: z.string().min(1).max(100).nullable().optional(),
    address_state: auStateCodeEnum.nullable().optional(),
    address_postcode: z
        .string()
        .regex(/^\d{4}$/u, 'AU postcode must be 4 digits')
        .nullable()
        .optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'patch must update at least one field',
});
/**
 * `PATCH /me/profile` response — the post-update person projection so
 * the SPA can refresh the form without a follow-up `GET /me`.
 */
export const meProfileUpdateResponseSchema = z.object({
    person: meSelfPersonSchema,
});
/**
 * One compliance record summarised for the volunteer's view. Sourced
 * from `compliance_records` joined to `compliance_requirements`.
 * Volunteer never sees verifier identifiers (those are flattened into
 * `verified_by_display` here and in `trainingChecklistVerifiedDetails`).
 */
export const meComplianceRecordSummarySchema = z.object({
    record_id: idSchema,
    requirement_id: idSchema,
    requirement_short_code: z.string().min(1),
    requirement_name: z.string().min(1),
    status: complianceRecordStatusEnum,
    completed_on: isoDateSchema.nullable(),
    expiry_date: isoDateSchema.nullable(),
    verified_on: isoDateTimeSchema.nullable(),
    verified_by_display: z.string().min(1).nullable(),
});
/** Status banner colour (PRD 06 §`/me` §2 Status banner). */
export const meStatusBannerKindEnum = z.enum(['green', 'amber', 'red']);
export const meStatusBannerSchema = z.object({
    kind: meStatusBannerKindEnum,
    message: z.string().min(1),
});
/**
 * One row on the `/me` Attestations pane. Sourced from `compliance_records`
 * joined to `compliance_requirements` for `pattern='volunteer_attestation'`
 * (migration 0026; PRD 01 §4a). Pending and verified-current rows both
 * render — verified rows show a "next due" hint so the volunteer can
 * re-confirm early if they want.
 *
 * `state` collapses the underlying `compliance_record_status` to the
 * three states the pane actually branches on:
 *   - `pending` — `compliance_records` row is `pending` or `expired`
 *     (both let the volunteer click confirm).
 *   - `verified` — `verified` and not yet within the cadence's first
 *     reminder offset; the row is informational, the button still
 *     renders so re-confirming early is one click.
 *   - `expiring_soon` — `verified` and within `-30` of expiry; same
 *     button + an "expires on …" hint.
 */
export const meAttestationStateEnum = z.enum(['pending', 'verified', 'expiring_soon']);
export const meAttestationRowSchema = z.object({
    requirement_id: idSchema,
    short_code: z.string().min(1),
    name: z.string().min(1),
    /** Verbatim admin-authored copy from `compliance_requirements.markdown_explainer`.
     *  The web SPA sanitises and renders it; markdown links targeting `/me/*`
     *  open in-app. */
    markdown_explainer: z.string(),
    /** The button copy the volunteer clicks. Sourced from
     *  `compliance_requirements.confirm_button_label`. */
    confirm_button_label: z.string().min(1),
    state: meAttestationStateEnum,
    /** Set on verified-current rows so the SPA can render "next due on …";
     *  null when the row is already pending. */
    expiry_date: isoDateSchema.nullable(),
    /** Set on the most recent confirm; null on first-time pending rows. */
    last_verified_on: isoDateTimeSchema.nullable(),
});
/**
 * Top-level `GET /me` response. Composes the per-section DTOs PRD 06
 * §`/me` calls out (header → status banner → training checklist →
 * attestations → acknowledgement history → reminder history). The
 * training-checklist shape lives in `training-checklist.ts` so M12b
 * can consume it without dragging the rest of the API surface in.
 */
/**
 * One row on the `/me` pending-acknowledgements pane (M13). Sourced from
 * `compliance_records` joined to `compliance_requirements` for
 * `pattern='sign'` rows in pending / expired status. The volunteer walks
 * the full read → type-name → step-up → confirm sub-flow inline on `/me`.
 */
export const mePendingAckRowSchema = z.object({
    compliance_record_id: idSchema,
    document_id: idSchema,
    document_title: z.string().min(1),
    version: z.string().min(1),
    /** Reason this row is pending — used for the "needs first ack" vs
     *  "republished, please re-acknowledge" copy split. `expired` covers
     *  the post-material-change wave; `pending` is "you've never
     *  acknowledged this policy". */
    reason: z.enum(['pending', 'expired']),
});
export const meDashboardResponseSchema = z.object({
    person: meSelfPersonSchema,
    status_banner: meStatusBannerSchema,
    training_checklist: z.array(trainingChecklistRowSchema),
    /** `pattern='volunteer_attestation'` rows for this volunteer. Empty
     *  when no attestation requirement is in the catalogue. */
    attestations: z.array(meAttestationRowSchema),
    /** Pending policy acknowledgements (M13). One row per pending /
     *  expired `compliance_records` whose requirement uses `pattern='sign'`.
     *  Drives the inline acknowledgement pane on `/me`; volunteers click
     *  Sign to walk through the read → type-name → step-up → confirm
     *  sub-flow without ever leaving `/me`. */
    pending_acknowledgements: z.array(mePendingAckRowSchema),
    records: z.array(meComplianceRecordSummarySchema),
    acknowledgement_history: z.array(meAcknowledgementHistoryEntrySchema),
    reminder_history: z.array(meReminderHistoryEntrySchema),
});
/**
 * `POST /me/attestations/:requirementId/confirm` — the volunteer clicks
 * the configured confirm button on `/me`. No request body; the path
 * param identifies the requirement. Server validates the session-bound
 * person has an applicable record, flips it to `verified` in one
 * transaction, and writes a paired `audit_events` row. Returns the
 * updated row so the SPA reconciles its optimistic flip.
 */
export const meAttestationConfirmResponseSchema = z.object({
    attestation: meAttestationRowSchema,
});
/**
 * Volunteer-side upload-URL request. The API verifies the record belongs
 * to the session's person before returning a pre-signed PUT.
 */
export const meEvidenceRequestUrlRequestSchema = z.object({
    compliance_record_id: idSchema,
    /**
     * Echoed back into the audit row so admins reviewing later can trace
     * which file the volunteer believed they were uploading. Validated
     * against the item type's allowlist server-side (PRD 06 §Upload flow).
     */
    filename: z.string().min(1).max(255),
    mime_type: z.string().min(1),
    size_bytes: z
        .number()
        .int()
        .positive()
        .max(20 * 1024 * 1024),
});
export const meEvidenceRequestUrlResponseSchema = z.object({
    upload_url: z.string().url(),
    storage_key: z.string().min(1),
    /** Short-expiry; client should PUT immediately. */
    expires_at: isoDateTimeSchema,
    /** Echoed back so the client hands it to /me/evidence/confirm. */
    compliance_record_id: idSchema,
});
/**
 * Confirm step — the API fetches the object, hashes it, validates type
 * and size, writes the `evidence` row, links the record, transitions to
 * `submitted` (PRD 04 §Self-service portal step 5).
 */
export const meEvidenceConfirmRequestSchema = z.object({
    compliance_record_id: idSchema,
    storage_key: z.string().min(1),
});
export const meEvidenceConfirmResponseSchema = z.object({
    evidence_id: idSchema,
    record_id: idSchema,
    /** Will always be `submitted` on success — exposed so the client can
     *  optimistically update the badge without a follow-up GET /me. */
    record_status: complianceRecordStatusEnum,
    sha256: hex64Schema,
});
// ===========================================================================
// People (admin) — POST /people, GET /people, GET /people/:id,
//                  PATCH /people/:id, POST /people/:id/archive
// ===========================================================================
/**
 * Admin person-creation request. Subset of `personSchema` excluding
 * server-managed fields (`person_id`, status defaults, audit timestamps,
 * `deleted_at`).
 */
export const createPersonRequestSchema = z
    .object({
    /** Required name parts — the API used to also accept a `full_name`
     *  blob and split it server-side, but migration 0034 dropped the
     *  stored full_name column and the create form takes the parts
     *  directly. The DTO `full_name` field on responses is projected
     *  from these via `concat_ws`. */
    first_name: z.string().min(1),
    middle_name: z.string().min(1).nullable().optional(),
    last_name: z.string().min(1),
    preferred_name: z.string().min(1).nullable(),
    /** Null at create — the volunteer fills it in at intake (PRD 06
     *  §Volunteer experience #1). Admin can later patch it. */
    date_of_birth: isoDateSchema.nullable(),
    contact_email: citextEmailSchema,
    person_type: personTypeEnum,
    role_tags: z.array(z.string().min(1)),
    direct_child_contact: z.boolean(),
    /** Optional at create — the admin supplies it if known. Otherwise it
     *  remains null until the volunteer confirms at intake or an admin
     *  patches it via the person-detail edit panel. */
    start_date: isoDateSchema.nullable(),
    end_date: isoDateSchema.nullable(),
    /**
     * Initial status — admin chooses `onboarding` (sends intake link) or
     * `active` (skips intake; e.g. when admin uploads on behalf at create
     * time). Other statuses are reachable only through later transitions.
     */
    initial_status: z.enum(['onboarding', 'active']),
    notes: z.string().nullable(),
    // NQA ITS Worker Register fields (migration 0019). All optional at
    // create — admin can fill them in here, at intake, or via PATCH.
    // The DB CHECK constraints on (nqa_position / non_educator_role) and
    // (wwcc_exemption / wwcc_exemption_reason) are mirrored in the
    // refines below for friendlier 400 errors.
    title: z.string().min(1).nullable().optional(),
    names_previously_known_as: z.string().min(1).nullable().optional(),
    alias_other_names_known_by: z.string().min(1).nullable().optional(),
    /** Single phone slot — used for both ACECQA portal phone fields. */
    mobile_phone: z.string().min(1).nullable().optional(),
    nqa_position: nqaPositionEnum.nullable().optional(),
    non_educator_role: nqaNonEducatorRoleEnum.nullable().optional(),
    /** DB defaults to 'Direct'; omit to inherit. */
    nature_of_employment: nqaNatureOfEmploymentEnum.optional(),
    on_probationary_period: z.boolean().nullable().optional(),
    /** DB defaults to false; omit to inherit. */
    wwcc_exemption: z.boolean().optional(),
    wwcc_exemption_reason: z.string().min(1).nullable().optional(),
    /** DB defaults to false; omit to inherit. */
    no_qualification_training: z.boolean().optional(),
    address_type: nqaAddressTypeEnum.nullable().optional(),
    address_line_1: z.string().min(1).nullable().optional(),
    address_line_2: z.string().min(1).nullable().optional(),
    address_suburb: z.string().min(1).nullable().optional(),
    address_state: auStateCodeEnum.nullable().optional(),
    address_postcode: z.string().min(1).nullable().optional(),
})
    .refine((v) => v.non_educator_role == null || v.nqa_position === 'Non-Educator Staff', {
    message: "non_educator_role may only be set when nqa_position is 'Non-Educator Staff'",
    path: ['non_educator_role'],
})
    .refine((v) => v.wwcc_exemption_reason == null || v.wwcc_exemption === true, {
    message: 'wwcc_exemption_reason may only be set when wwcc_exemption is true',
    path: ['wwcc_exemption_reason'],
});
export const createPersonResponseSchema = z.object({
    person: personSchema,
});
/**
 * Admin people-list filter shape. The "saved views" PRD 06 calls out
 * (active / onboarding / inactive / stood_down) are just particular
 * `status` values. `q` is a substring search on full name + email,
 * intentionally fuzzy.
 */
export const peopleListQuerySchema = paginationParamsSchema.extend({
    status: personStatusEnum.optional(),
    person_type: personTypeEnum.optional(),
    q: z.string().min(1).max(200).optional(),
    /** When true, includes soft-deleted rows. Default false. */
    include_deleted: querystringBool.default(false),
});
export const peopleListResponseSchema = paginatedResponseSchema(personSchema);
/**
 * GET /people/:id — the full person row plus joined records, plus a
 * derived flag the person-detail screen uses to nudge the admin to
 * mark NQA ITS as synced when something has changed since the last
 * sync (PRD 06 §People list — detail view).
 *
 * `needs_nqa_its_sync` is true when `nqa_its_synced_at` is null OR
 * any regulator-relevant change (record verify/supersede, person
 * patch, archive) has bumped `people.updated_at` past
 * `nqa_its_synced_at`.
 */
export const personDetailResponseSchema = z.object({
    person: personSchema,
    records: z.array(complianceRecordSchema),
    needs_nqa_its_sync: z.boolean(),
});
/**
 * `POST /people/:id/nqa-its-sync` — admin marks the person's NQA ITS
 * portal entry as up-to-date after completing the manual portal
 * sync. Optionally captures the freshly-issued `nqa_its_entry_id`.
 */
export const markNqaItsSyncedRequestSchema = z.object({
    nqa_its_entry_id: z.string().min(1).nullable().optional(),
});
export const markNqaItsSyncedResponseSchema = z.object({
    person: personSchema,
});
/**
 * PATCH /people/:id — every field optional. The email-change cascade
 * fires server-side when `contact_email` is in the patch (PRD 04
 * §Self-service portal "Email change protection").
 */
export const updatePersonRequestSchema = z
    .object({
    preferred_name: z.string().min(1).nullable().optional(),
    date_of_birth: isoDateSchema.optional(),
    contact_email: citextEmailSchema.optional(),
    role_tags: z.array(z.string().min(1)).optional(),
    direct_child_contact: z.boolean().optional(),
    start_date: isoDateSchema.optional(),
    end_date: isoDateSchema.nullable().optional(),
    status: personStatusEnum.optional(),
    notes: z.string().nullable().optional(),
    // NQA ITS Worker Register fields (migration 0019). All optional in a
    // PATCH; the CHECK-mirror refines below only fire when both halves
    // of a constrained pair are present in the same patch (since partial
    // updates can legally land one half at a time as long as the
    // resulting row still satisfies the DB CHECK).
    title: z.string().min(1).nullable().optional(),
    first_name: z.string().min(1).optional(),
    middle_name: z.string().min(1).nullable().optional(),
    last_name: z.string().min(1).optional(),
    names_previously_known_as: z.string().min(1).nullable().optional(),
    alias_other_names_known_by: z.string().min(1).nullable().optional(),
    mobile_phone: z.string().min(1).nullable().optional(),
    nqa_position: nqaPositionEnum.nullable().optional(),
    non_educator_role: nqaNonEducatorRoleEnum.nullable().optional(),
    nature_of_employment: nqaNatureOfEmploymentEnum.optional(),
    on_probationary_period: z.boolean().nullable().optional(),
    wwcc_exemption: z.boolean().optional(),
    wwcc_exemption_reason: z.string().min(1).nullable().optional(),
    no_qualification_training: z.boolean().optional(),
    address_type: nqaAddressTypeEnum.nullable().optional(),
    address_line_1: z.string().min(1).nullable().optional(),
    address_line_2: z.string().min(1).nullable().optional(),
    address_suburb: z.string().min(1).nullable().optional(),
    address_state: auStateCodeEnum.nullable().optional(),
    address_postcode: z.string().min(1).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'patch must update at least one field',
})
    .refine((v) => {
    // Only fire when both halves are in the patch — partial patches
    // that touch only one half can't be validated wire-side because
    // the other half's current DB value is unknown to zod. The
    // server-side handler also applies the same check post-merge,
    // and the DB CHECK is the ultimate enforcement either way.
    if (v.non_educator_role === undefined || v.nqa_position === undefined)
        return true;
    return v.non_educator_role == null || v.nqa_position === 'Non-Educator Staff';
}, {
    message: "non_educator_role may only be set when nqa_position is 'Non-Educator Staff'",
    path: ['non_educator_role'],
})
    .refine((v) => {
    if (v.wwcc_exemption_reason === undefined || v.wwcc_exemption === undefined)
        return true;
    return v.wwcc_exemption_reason == null || v.wwcc_exemption === true;
}, {
    message: 'wwcc_exemption_reason may only be set when wwcc_exemption is true',
    path: ['wwcc_exemption_reason'],
});
/**
 * `POST /people/:id/archive` — single-step transition. Flips the person
 * to `status='inactive'`, sets `end_date`, archives all non-terminal
 * compliance records, and revokes every active `volunteer_sessions` row
 * (PRD 04 §Identity and access, PRD 06 §People list). The admin SPA's
 * confirm modal is the "are you sure" gate; the API does not model it as
 * a separate `pending_archive` state.
 */
export const archivePersonRequestSchema = z.object({
    reason: z.string().min(1),
});
export const archivePersonResponseSchema = z.object({
    person: personSchema,
    records_archived: z.number().int().nonnegative(),
    sessions_revoked: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Person history timeline — GET /people/:personId/history
// ---------------------------------------------------------------------------
/**
 * Chronological history timeline for the admin person-detail page (PRD 06
 * §People list — detail view: "every reminder sent and every audit event
 * touching this person"). Each row carries a discriminated `kind` so the
 * UI renders the appropriate icon + summary copy without re-parsing the
 * `action` string.
 */
export const personHistoryReminderEntrySchema = z.object({
    kind: z.literal('reminder'),
    reminder_id: idSchema,
    record_id: idSchema,
    /** Requirement display name joined from `compliance_requirements`. */
    requirement_name: z.string().min(1),
    requirement_short_code: z.string().min(1),
    template: z.string().min(1),
    occurred_at: isoDateTimeSchema,
    /** Negative offsets are post-expiry escalations (PRD 02). */
    offset_days: z.number().int(),
    delivery_status: z.enum(['sent', 'bounced', 'failed']),
});
/**
 * Audit-event timeline entry. The actor is hydrated server-side: admin
 * actors carry their `full_name` so the UI doesn't need a second
 * lookup; volunteer actors are labelled with the person's name; system
 * actors render as "System". `entity_summary` is a short pre-formatted
 * label (e.g. "Compliance record #42") — the UI links the row to the
 * relevant detail screen when `entity_table` is one we know about.
 */
export const personHistoryAuditEntrySchema = z.object({
    kind: z.literal('audit_event'),
    audit_event_id: idSchema,
    occurred_at: isoDateTimeSchema,
    action: z.string().min(1),
    entity_table: z.string().min(1),
    entity_id: idSchema.nullable(),
    actor_kind: z.enum(['admin', 'volunteer', 'system']),
    /** Display name for human actors; null for system actors. */
    actor_display: z.string().min(1).nullable(),
    /** Short human-readable label, e.g. "Compliance record #42" — pre-formatted server-side. */
    entity_summary: z.string().min(1),
    /** Truthy when the SPA should render this row as a link to a known detail screen. */
    entity_link: z
        .object({
        kind: z.enum(['compliance_record', 'person', 'evidence', 'acknowledgement_signature']),
        id: idSchema,
    })
        .nullable(),
});
export const personHistoryEntrySchema = z.discriminatedUnion('kind', [
    personHistoryReminderEntrySchema,
    personHistoryAuditEntrySchema,
]);
/**
 * `GET /people/:personId/history` — combined timeline. `?limit` caps the
 * total returned rows (default 100, max 500); the response carries a
 * `truncated` flag when the underlying union exceeded the cap so the UI
 * can hint that older history exists. No cursor pagination in v1 — for
 * the timeline view a single bounded read is simpler than paging
 * through merged streams, and an admin who needs older rows can use the
 * audit-log export (US-ADM-13).
 */
export const personHistoryQuerySchema = z.object({
    limit: z.coerce.number().int().positive().max(500).default(100),
});
export const personHistoryResponseSchema = z.object({
    items: z.array(personHistoryEntrySchema),
    /** True when more history exists beyond the returned `limit`. */
    truncated: z.boolean(),
    /** Total candidate rows considered before truncation. */
    total_candidates: z.number().int().nonnegative(),
});
// ===========================================================================
// Compliance requirements — GET, POST, PATCH, bulk-invalidate (M10 rework).
// Renamed from `compliance-item-types` in migration 0023; the eight
// substantive fields replace the old wide schema.
// ===========================================================================
export const complianceRequirementsListQuerySchema = z.object({
    active: querystringBool.optional(),
});
export const complianceRequirementsListResponseSchema = z.object({
    items: z.array(complianceRequirementSchema),
});
/**
 * Admin requirement creation — the slim shape after M10's rework. Per
 * PRD 01 §5 + PRD 03 §2, eight substantive fields plus server-managed
 * timestamps. Cadence is hard-coded in the worker; per-row
 * `reminder_offsets`, `escalation_offsets`, `still_active_question`, etc.
 * are gone.
 */
export const createComplianceRequirementRequestSchema = z.object({
    name: z.string().min(1),
    short_code: z.string().min(1),
    pattern: requirementPatternEnum,
    active: z.boolean(),
    markdown_explainer: z.string(),
    /** Required when `pattern === 'sign'`; null otherwise. The route also
     *  enforces this and the DB has a matching CHECK. */
    linked_document_id: idSchema.nullable(),
    expiry_duration_days: z.number().int().positive().nullable(),
    /** Optional at create — defaults to the DB default `'I confirm'`.
     *  Surfaced on the volunteer's `/me` attestation row when
     *  `pattern='volunteer_attestation'` and quoted in the daily-reminder
     *  email body so the volunteer sees the same wording in both places. */
    confirm_button_label: z.string().min(1).optional(),
});
export const updateComplianceRequirementRequestSchema = createComplianceRequirementRequestSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, {
    message: 'patch must update at least one field',
});
/**
 * Bulk-invalidate — admin nullifies every currently-verified record for
 * a given requirement. Replaces the implicit auto-expire-on-document-
 * version-bump that the old `fixed_review_cycle` strategy provided
 * (PRD 02 §Bulk-invalidate). `expected_count` is the race-protection
 * knob: the SPA shows the count, the admin confirms, and if the actual
 * count drifted (someone verified a record between the page load and
 * the click) the server returns 409 so the admin re-confirms instead
 * of silently invalidating an extra row.
 */
export const bulkInvalidateRequirementRequestSchema = z.object({
    reason: z.string().min(1).max(2000),
    expected_count: z.number().int().nonnegative(),
});
export const bulkInvalidateRequirementResponseSchema = z.object({
    invalidated_count: z.number().int().nonnegative(),
});
// ===========================================================================
// Compliance records (admin) — list, get, create, verify, supersede
// ===========================================================================
/**
 * Filter views surfaced on the admin SPA dashboard (PRD 06 §Dashboard,
 * PRD 04 §API surface). `view` is a coarse named filter; `status` /
 * `person_id` / `requirement_id` further narrow.
 */
export const complianceRecordsListQuerySchema = paginationParamsSchema.extend({
    view: z
        .enum(['expiring_in_30', 'expired_overdue', 'new_this_week', 'awaiting_verification'])
        .optional(),
    status: complianceRecordStatusEnum.optional(),
    person_id: idSchema.optional(),
    requirement_id: idSchema.optional(),
});
/**
 * Slim person projection inlined on the compliance-records list rows so
 * the admin SPA's `/admin/compliance-records` table renders name + status
 * chips without a second round-trip per row (PRD 06 §Dashboard drill-down).
 */
export const complianceRecordListPersonSchema = z.object({
    person_id: idSchema,
    full_name: z.string().min(1),
    preferred_name: z.string().min(1).nullable(),
    status: personStatusEnum,
});
/**
 * Slim requirement projection inlined on the compliance-records list rows.
 * The full admin-configurable row lives at
 * `GET /compliance-requirements/:requirementId`.
 */
export const complianceRecordListRequirementSchema = z.object({
    requirement_id: idSchema,
    name: z.string().min(1),
    short_code: z.string().min(1),
    pattern: requirementPatternEnum,
});
/**
 * Each row in the compliance-records list response. The record fields
 * remain at the top level to preserve back-compat with existing callers
 * that read `items[i].status` / `items[i].person_id` directly; the nested
 * `person` and `requirement` objects are additive and carry the display
 * names / badges the list UI needs.
 */
export const complianceRecordListRowSchema = complianceRecordSchema.extend({
    person: complianceRecordListPersonSchema,
    requirement: complianceRecordListRequirementSchema,
});
export const complianceRecordsListResponseSchema = paginatedResponseSchema(complianceRecordListRowSchema);
/**
 * Create-on-submit (admin path). The volunteer self-service equivalent
 * is `POST /me/evidence/confirm`, which folds creation + evidence
 * linking into one call.
 */
export const createComplianceRecordRequestSchema = z.object({
    person_id: idSchema,
    requirement_id: idSchema,
    /** Optional pre-existing evidence_id (admin "Upload on behalf" flow). */
    evidence_id: idSchema.nullable(),
    completed_on: isoDateSchema.nullable(),
});
/**
 * Verify endpoint — the admin's primary action. WWCC has the extended
 * `outcome` branch (PRD 01 §1, PRD 06 §WWCC help panel). For non-WWCC
 * item types, `outcome` is `valid` (the only forward-progress value).
 */
export const verifyComplianceRecordRequestSchema = z.object({
    verification_notes: z.string().min(1).nullable(),
    /** Defaults to the strategy resolver's output; admin override allowed
     *  for items where the evidence implies a different date. */
    expiry_date_override: isoDateSchema.nullable(),
    /** WWCC-specific: portal verification outcome. Other requirements pass
     *  `valid` (the API's "I confirm this evidence is good") sentinel. */
    outcome: z.enum(['valid', 'barred', 'interim_bar', 'not_found']).default('valid'),
    /** WWCC-specific: prohibition-notice check folded into the WWCC row
     *  for day-one rollout (PRD 06 §WWCC help panel "Prohibition-notice
     *  check folded in on day-one rollout"). */
    prohibition_notice_check_completed: z.boolean().nullable(),
});
export const verifyComplianceRecordResponseSchema = z.object({
    record: complianceRecordSchema,
    /** When `outcome !== 'valid'`, the API may also stand the person down
     *  and queue an escalation email; surfacing the resulting status here
     *  lets the SPA refresh without a follow-up GET. */
    person_status: personStatusEnum,
});
/**
 * `GET /compliance-records/:recordId/detail` — one-call hydrate for the
 * admin verification workflow screen (M13, PRD 06 §Verification workflow,
 * US-ADM-03 + US-ADM-04). Joins the record with the person (for the
 * side-by-side name match), the requirement (for the pattern + custom
 * fields so the SPA can render the right form + help panel), and — when
 * the record points at an evidence row — a freshly-presigned download
 * URL for the inline evidence preview. Admin-only.
 *
 * `evidence` is `null` for records whose evidence has not yet been
 * uploaded (typed-name acknowledgement records carry
 * `acknowledgement_signature_id` instead and have `evidence: null`) or
 * when the admin is opening a `pending`-status record. The SPA branches
 * on this to render either the inline preview + verify form or a
 * "waiting for volunteer upload" panel.
 */
export const complianceRecordEvidencePreviewSchema = z.object({
    evidence: evidenceSchema,
    /** Short-expiry presigned GET URL that renders inline in the `<iframe>`
     *  (PDF) or `<img>` (PNG/JPEG) preview. The API re-issues a fresh URL
     *  on every detail fetch so the preview never serves a stale link. */
    download_url: z.string().url(),
    /** ISO timestamp when `download_url` stops working — useful to show a
     *  "refresh" hint if the admin stays on the page past the expiry. */
    download_url_expires_at: isoDateTimeSchema,
});
/**
 * Slim person projection for the verification screen header. The full
 * admin person row is admin-accessible via `GET /people/:personId` — the
 * detail endpoint only surfaces the fields the verification UI needs for
 * the side-by-side name / DOB / email check (PRD 06 §Verification
 * workflow step 2, US-ADM-03 name-check requirement).
 */
export const complianceRecordPersonSummarySchema = z.object({
    person_id: idSchema,
    full_name: z.string().min(1),
    preferred_name: z.string().min(1).nullable(),
    /** Null when the admin hasn't captured it yet and the volunteer has
     *  not completed intake (PRD 03 §1 — DOB is volunteer-supplied). */
    date_of_birth: isoDateSchema.nullable(),
    contact_email: citextEmailSchema,
    person_type: personTypeEnum,
    status: personStatusEnum,
    direct_child_contact: z.boolean(),
});
/**
 * Candidate rows for the supersede picker on the verification-screen
 * detail response (PRD 03 §3, M13 — "supersede affordance inline on the
 * per-record list"). The admin on the detail page of an outgoing record
 * picks the replacement record from this list; the API pre-filters to
 * the same person + item type and excludes terminal statuses
 * (`superseded`, `archived`) so the picker never shows a row that
 * would 400 on submit.
 *
 * Ordered newest-first in the response so the most-likely replacement
 * sits at the top.
 */
export const complianceRecordSupersedeCandidateSchema = z.object({
    record_id: idSchema,
    status: complianceRecordStatusEnum,
    completed_on: isoDateSchema.nullable(),
    expiry_date: isoDateSchema.nullable(),
    verified_on: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
});
export const complianceRecordDetailResponseSchema = z.object({
    record: complianceRecordSchema,
    person: complianceRecordPersonSummarySchema,
    requirement: complianceRequirementSchema,
    evidence: complianceRecordEvidencePreviewSchema.nullable(),
    /** Records eligible to supersede the current record (same person,
     *  same item type, not terminal, not the record itself). Empty array
     *  when no candidate exists yet — the SPA renders the panel with a
     *  "create a new record first" hint in that case. */
    supersede_candidates: z.array(complianceRecordSupersedeCandidateSchema),
});
export const supersedeComplianceRecordRequestSchema = z.object({
    /** The new record to mark as superseding the path-param record. */
    superseded_by_id: idSchema,
});
/**
 * `POST /compliance-records/:recordId/request-reverification` — the WWCC
 * help-panel hand-off for `Not Found` and name/DOB mismatch outcomes
 * (PRD 06 §WWCC help panel — unusual outcomes, US-ADM-05). The record
 * stays in `submitted` status; the admin captures the reason in a
 * short note; server writes a paired `compliance_record.request_reverification`
 * audit row and (future M14 enqueue site) mails the volunteer asking
 * them to confirm their WWCC number + DOB. No person-status change.
 *
 * `reason` is one of the four documented WWCC branches so the audit
 * row carries a stable categorisation; `note` is free-form context
 * the next admin sees when they reopen the record.
 */
export const requestReverificationReasonEnum = z.enum([
    'wwcc_not_found',
    'wwcc_name_mismatch',
    'wwcc_dob_mismatch',
    'other',
]);
export const requestReverificationRequestSchema = z.object({
    reason: requestReverificationReasonEnum,
    note: z.string().min(1).max(2000),
});
export const requestReverificationResponseSchema = z.object({
    record: complianceRecordSchema,
});
/**
 * `POST /compliance-records/:recordId/attach-evidence` — admin "Upload on
 * behalf" link step (PRD 06 §Edge cases, M13). The admin first uploads
 * the file via `POST /uploads/request-url` + `POST /uploads/confirm` to
 * mint an `evidence` row; this endpoint then links that row onto a
 * specific compliance_records row in one transaction, transitioning
 * `pending → submitted` (or replacing `evidence_id` on a record that's
 * already `submitted`). The required `on_behalf_note` lands in
 * `verification_notes` so the next admin verifying sees the upload
 * context, and is also captured verbatim in the paired audit row.
 *
 * Records that are already in a terminal status (`verified`,
 * `superseded`, `archived`) reject with
 * `invalid_status_transition` — once a record is verified the verify
 * endpoint owns `evidence_id`. Evidence that doesn't belong to the
 * record's `(person_id, requirement_id)` rejects with `evidence_not_found`
 * so accidental cross-linking can't happen.
 */
export const attachEvidenceToRecordRequestSchema = z.object({
    evidence_id: idSchema,
    on_behalf_note: z.string().min(1).max(2000),
});
export const attachEvidenceToRecordResponseSchema = z.object({
    record: complianceRecordSchema,
});
// ===========================================================================
// Evidence (admin) — request upload URL, confirm upload, download URL
// ===========================================================================
/**
 * Admin-side upload URL request — equivalent to the volunteer's
 * `POST /me/evidence` but scoped to a `person_id` + `requirement_id`
 * rather than a record id (admin may be creating the record alongside).
 */
export const requestUploadUrlRequestSchema = z.object({
    person_id: idSchema,
    requirement_id: idSchema,
    filename: z.string().min(1).max(255),
    mime_type: z.string().min(1),
    size_bytes: z
        .number()
        .int()
        .positive()
        .max(20 * 1024 * 1024),
});
export const requestUploadUrlResponseSchema = z.object({
    upload_url: z.string().url(),
    storage_key: z.string().min(1),
    upload_session_id: z.string().min(1),
    expires_at: isoDateTimeSchema,
});
export const confirmUploadRequestSchema = z.object({
    upload_session_id: z.string().min(1),
    storage_key: z.string().min(1),
    /**
     * Admin-side only: required note explaining why admin uploaded on the
     * volunteer's behalf — surfaces in `verification_notes` (PRD 06 §Edge
     * cases, M13 "Admin Upload on behalf").
     */
    on_behalf_note: z.string().min(1).nullable(),
});
export const confirmUploadResponseSchema = z.object({
    evidence: evidenceSchema,
});
export const evidenceDownloadUrlResponseSchema = z.object({
    download_url: z.string().url(),
    expires_at: isoDateTimeSchema,
});
// ===========================================================================
// Documents — list, create, archive, get PDF URL
// ===========================================================================
export const documentsListQuerySchema = paginationParamsSchema.extend({
    /** When true, includes archived rows in the result. Default false —
     *  the page only surfaces active versions. */
    include_archived: querystringBool.default(false),
});
export const documentsListResponseSchema = paginatedResponseSchema(documentSchema);
/**
 * Create document — one-shot: the admin first uploads a pre-rendered
 * policy PDF via `POST /admin/documents/upload-url`, then calls this
 * endpoint with the resulting storage key plus `title` and `version`.
 * The handler hashes the bytes server-side and INSERTs a fully-formed
 * active row in one go — there is no separate publish step. The SHA-256
 * captured here is the audit anchor every acknowledgement signature
 * carries forward via `document_sha256`.
 */
export const createDocumentRequestSchema = z.object({
    title: z.string().min(1),
    version: z.string().min(1),
    /** Storage key of the freshly-uploaded PDF (returned by the
     *  upload-URL endpoint). Server fetches and hashes the bytes. */
    rendered_pdf_storage_key: z.string().min(1),
});
/**
 * Archive — retire a no-longer-current version. Idempotent: if the row
 * is already archived, returns the row as-is. The handler refuses
 * (409 `document_in_use_by_requirement`) when any active
 * `compliance_requirements.linked_document_id` still points at it —
 * the operator must repoint the requirement first. Existing
 * `acknowledgement_signatures.document_id` references stay; archiving
 * is metadata-only and the audit chain is preserved.
 */
export const archiveDocumentResponseSchema = z.object({
    document: documentSchema,
});
export const unarchiveDocumentResponseSchema = z.object({
    document: documentSchema,
});
export const documentPdfUrlResponseSchema = z.object({
    pdf_url: z.string().url(),
    expires_at: isoDateTimeSchema,
    rendered_pdf_sha256: hex64Schema,
});
/**
 * `POST /admin/documents/upload-url` — admin-side request for a presigned
 * PUT URL that lets the SPA stream a pre-rendered PDF directly into
 * object storage under `documents/rendered/{uuid}.pdf`. Step 1 of the
 * SPA upload flow: (1) request URL → (2) browser PUT to the URL →
 * (3) `POST /admin/documents` with the returned `storage_key` to register
 * the document row + capture the PDF's SHA-256 (PRD 03 §7, PRD 04 §Stack
 * summary). MIME type is pinned to PDF by the server.
 */
export const DOCUMENT_PDF_MIME_TYPE = 'application/pdf';
export const MAX_DOCUMENT_PDF_BYTES = 20 * 1024 * 1024;
export const requestDocumentUploadUrlRequestSchema = z.object({
    filename: z.string().min(1).max(255),
    size_bytes: z.number().int().positive().max(MAX_DOCUMENT_PDF_BYTES),
});
export const requestDocumentUploadUrlResponseSchema = z.object({
    upload_url: z.string().url(),
    storage_key: z.string().min(1),
    expires_at: isoDateTimeSchema,
});
// ===========================================================================
// Acknowledgements — inline `/me` flow with email step-up code (M13)
// ===========================================================================
//
// Replaces the per-delivery `/ack/:token` URL. The volunteer signs into
// `/me`, opens a pending acknowledgement, types their name, requests a
// fresh 6-digit code emailed to `contact_email`, then types the code
// back into the same page. The code is consumed in the same transaction
// that writes the signature row; the consumed `magic_link_tokens.token_jti`
// becomes the per-event identification factor pinned onto the signature
// (PRD 05 §4, migration 0029).
/**
 * Confirmations object captured on submit — PRD 03 §5 records this in
 * `acknowledgement_signatures.confirmations`. Schema is open-ended (the
 * audit row stores it verbatim) but these keys are the ones the M13
 * flow writes.
 */
export const ackSubmitConfirmationsSchema = z.object({
    read_via: z.enum(['inline_scroll', 'external_viewer']),
    scrolled_to_end: z.boolean(),
    understands_legal_equivalence: z.boolean(),
    /** True when the volunteer accepted a near-match name override. */
    name_override: z.boolean(),
});
/**
 * `GET /me/acknowledgements` — the pending-acks pane on `/me`. One row per
 * `compliance_records` in pending / expired status whose requirement uses
 * `pattern='sign'` for the session's volunteer. Same shape as the
 * `pending_acknowledgements` field on `meDashboardResponseSchema` —
 * exposed as a standalone endpoint so the SPA can re-poll the pane
 * after a sign without re-fetching the whole dashboard payload.
 */
export const mePendingAckListResponseSchema = z.object({
    pending: z.array(mePendingAckRowSchema),
});
/**
 * `POST /me/ack/start` — open the read pane for one pending policy.
 * Returns the rendered PDF URL, the verbatim acknowledgement statement,
 * and a fresh server-side nonce the subsequent step-up call must echo.
 */
export const meAckStartRequestSchema = z.object({
    compliance_record_id: idSchema,
});
export const meAckStartResponseSchema = z.object({
    compliance_record_id: idSchema,
    document_id: idSchema,
    document_title: z.string().min(1),
    version: z.string().min(1),
    document_sha256: hex64Schema,
    document_storage_key: z.string().min(1),
    pdf_url: z.string().url(),
    pdf_url_expires_at: isoDateTimeSchema,
    acknowledgement_text: z.string().min(1),
    acknowledgement_text_version: z.string().min(1),
    /** Volunteer's name on file — pre-filled in the typed-name input so
     *  the volunteer can correct typos before submission. */
    full_name_on_file: z.string().min(1),
    /** Echoed back on the step-up + confirm calls so the read pane the
     *  volunteer saw is what their signing event commits against. */
    page_render_id: z.string().min(1),
});
/**
 * `POST /me/ack/step-up` — request the email code. Validates the typed
 * name against the name on file, mints a 6-digit code, hashes it,
 * stores it as a single-use `magic_link_tokens` row scoped to the
 * volunteer with `purpose='acknowledgement_step_up'`, and sends it to
 * the volunteer's `contact_email` inline. On a near-match the server
 * returns 400 `name_near_match_needs_override`; the SPA prompts and
 * resubmits with `confirmations.name_override = true`.
 */
export const meAckStepUpRequestSchema = z.object({
    compliance_record_id: idSchema,
    page_render_id: z.string().min(1),
    typed_name: z.string().min(1),
    confirmations: ackSubmitConfirmationsSchema,
    client_local_time: isoDateTimeSchema,
});
export const meAckStepUpResponseSchema = z.object({
    /** Server-issued attempt id the SPA echoes on the confirm call.
     *  Bound 1:1 to the freshly-minted `magic_link_tokens.token_jti`. */
    attempt_id: z.string().min(1),
    /** Seconds until the issued code expires. 5 minutes by default. */
    expires_in_seconds: z.number().int().positive(),
});
/**
 * `POST /me/ack/confirm` — atomic redeem-and-sign. Validates the typed
 * 6-digit code, marks the `magic_link_tokens` row used, writes the
 * `acknowledgement_signatures` row with `step_up_token_jti +
 * step_up_consumed_at`, transitions the `compliance_records` row to
 * `verified`/`system`, and writes the paired `audit_events` row — all
 * in one DB transaction.
 */
export const meAckConfirmRequestSchema = z.object({
    attempt_id: z.string().min(1),
    /** The 6-digit code the volunteer received in their inbox. */
    code: z.string().regex(/^\d{6}$/u, 'Code must be 6 digits'),
});
export const meAckConfirmResponseSchema = z.object({
    signature_id: idSchema,
    compliance_record_id: idSchema,
    submitted_at_utc: isoDateTimeSchema,
    /** Pre-signed URL to the policy PDF the volunteer just acknowledged.
     *  Short-expiry — the email receipt carries the longer-lived link. */
    receipt_url: z.string().url(),
});
// ===========================================================================
// Audit / export — POST /admin/exports/dashboard-and-audit, list, download
// ===========================================================================
/**
 * Audit-pack export request (PRD 05 §Audit pack, PRD 04 §Admin-only
 * download). Filters scope which audit-log entries and which
 * volunteers' evidence ship in the pack; defaults (everything) are
 * applied server-side. The earlier two-mode flow (a separate
 * "dashboard + audit log" Excel-only export, with a per-export
 * pre-signed download URL and a `resend` endpoint to re-mint it) was
 * retired April 2026 — operational reads come from the dashboard
 * directly; the regulator zip is the only export shape, downloaded
 * through a login-gated endpoint instead of a pre-signed URL.
 */
export const exportAuditPackRequestSchema = z.object({
    audit_log_from: isoDateSchema.nullable(),
    audit_log_to: isoDateSchema.nullable(),
    person_ids: z.array(idSchema).nullable(),
    requirement_ids: z.array(idSchema).nullable(),
});
export const exportAuditPackResponseSchema = z.object({
    /** scheduled_job_runs.job_run_id of the queued export. */
    job_run_id: idSchema,
    /** ID the admin SPA polls / displays in the Recent exports list. */
    export_request_id: z.string().min(1),
    status: jobRunStatusEnum,
});
/**
 * Recent-exports list entry. One row per audit-pack export the admin
 * has run. `download_path` is a stable login-gated path (e.g.
 * `/admin/exports/123/download`); the SPA renders it as a same-origin
 * `<a>` link and the browser includes the session cookie automatically.
 * Null until the job completes successfully — the SPA falls back to
 * status copy in the meantime.
 */
export const exportListEntrySchema = z.object({
    export_request_id: z.string().min(1),
    job_run_id: idSchema,
    status: jobRunStatusEnum,
    requested_at: isoDateTimeSchema,
    completed_at: isoDateTimeSchema.nullable(),
    /** Same-origin login-gated download path. Null until the job completes. */
    download_path: z.string().nullable(),
});
export const exportListResponseSchema = z.object({
    items: z.array(exportListEntrySchema),
});
// ===========================================================================
// Worker dashboard panels (M9 Worker simplification).
// ===========================================================================
/** One row in the cron-health panel. */
export const cronHealthEntrySchema = z.object({
    job_name: z.enum(['daily_reminder', 'weekly_digest']),
    expected_interval_minutes: z.number().int().positive(),
    grace_minutes: z.number().int().nonnegative(),
    last_succeeded_at: isoDateTimeSchema.nullable(),
    /** True when the most recent succeeded tick was a no-work skip
     *  (weekly digest only). */
    last_succeeded_skipped_empty: z.boolean(),
    last_failed_at: isoDateTimeSchema.nullable(),
    last_failed_message: z.string().nullable(),
    /** True when no successful tick has landed within the expected
     *  interval + grace. The dashboard renders this red. */
    stale: z.boolean(),
});
export const cronHealthResponseSchema = z.object({
    jobs: z.array(cronHealthEntrySchema),
});
/** One row in the mail-failures panel. */
export const mailFailureEntrySchema = z.object({
    failure_id: idSchema,
    to_email: z.string().min(1),
    template_name: z.string().min(1),
    rendered_subject: z.string(),
    error_message: z.string(),
    failed_at: isoDateTimeSchema,
    retried_at: isoDateTimeSchema.nullable(),
    retried_outcome: z.enum(['succeeded', 'failed']).nullable(),
    retried_error_message: z.string().nullable(),
});
export const mailFailuresListResponseSchema = z.object({
    items: z.array(mailFailureEntrySchema),
});
export const mailFailureRetryResponseSchema = z.object({
    outcome: z.enum(['sent', 'failed']),
    message_id: z.string().nullable(),
    error_message: z.string().nullable(),
});
export const mailFailureDeleteResponseSchema = z.object({
    deleted: z.literal(true),
});
/** One row in the stuck-audit-packs panel. */
export const stuckAuditPackEntrySchema = z.object({
    audit_pack_run_id: idSchema,
    status: z.enum(['pending', 'running', 'failed']),
    requested_at: isoDateTimeSchema,
    started_at: isoDateTimeSchema.nullable(),
    finished_at: isoDateTimeSchema.nullable(),
    error_message: z.string().nullable(),
    requested_by_admin_user_id: idSchema,
});
export const stuckAuditPacksResponseSchema = z.object({
    items: z.array(stuckAuditPackEntrySchema),
});
/**
 * Response from `POST /internal/cron/<job>` (EventBridge Scheduler →
 * App Runner HTTP trigger; see `issues/eventbridge-cron-trigger.md` and
 * PRD 04 §Scheduling and execution).
 *
 *   * `succeeded`       — the handler ran cleanly. `skipped_empty=true`
 *                         when the weekly digest had nothing to send.
 *   * `idempotent_skip` — a `cron_runs` row already exists for this
 *                         job's Sydney-local bucket; EventBridge retried.
 *                         No `run_id` because no row was inserted.
 *   * `failed`          — the handler threw; the `cron_runs` row is in
 *                         `status='failed'` with `error_message`.
 *                         The HTTP layer maps this to 500 so EventBridge
 *                         retries.
 *
 * The endpoint is internal — no SPA consumer — so the shape is sized for
 * curl smoke-tests and the integration tests, not external compatibility.
 */
export const cronTriggerResponseSchema = z.object({
    outcome: z.enum(['succeeded', 'idempotent_skip', 'failed']),
    run_id: idSchema.optional(),
    skipped_empty: z.boolean().optional(),
});
// ===========================================================================
// Reminders — read-only list view (PRD 04 §API surface)
// ===========================================================================
export const remindersListQuerySchema = paginationParamsSchema.extend({
    person_id: idSchema.optional(),
    record_id: idSchema.optional(),
    /** ISO date — sent_at >= this date. */
    since: isoDateSchema.optional(),
});
export const remindersListResponseSchema = paginatedResponseSchema(reminderSchema);
// ===========================================================================
// Admin dashboard — `GET /admin/dashboard`
//
// A single aggregated read that backs the `/admin` landing page (PRD 06
// §Dashboard, M13). Four compliance snapshots + the "Volunteers needing NQA
// ITS sync" snapshot + worker-job health + dead-letter count all ship in
// one response so the SPA hydrates the dashboard in one round trip. Each
// snapshot carries the full `total_count` plus a capped list of top rows
// (default 10) with a `truncated` flag — the dashboard always shows the
// true scale even when the inline list is shortened. Detailed drill-down
// is via the existing `/admin/compliance-records?view=...` endpoints; the
// NQA ITS panel drills into `/admin/people` filtered by needs-sync.
// ===========================================================================
/**
 * Compliance-record row projection used by the three dashboard snapshot
 * sections. Keeps the fields the SPA actually renders (person + item
 * name + expiry + age) without pulling the full `complianceRecordSchema`,
 * which carries verification metadata that isn't useful at the dashboard
 * row level.
 */
export const dashboardComplianceRowSchema = z.object({
    record_id: idSchema,
    person_id: idSchema,
    full_name: z.string().min(1),
    requirement_name: z.string().min(1),
    status: complianceRecordStatusEnum,
    expiry_date: isoDateSchema.nullable(),
    created_at: isoDateTimeSchema,
    /**
     * Signed days between `expiry_date` and today. Positive = days-until-expiry
     * (expiring soon sections), negative = days-overdue (expired section).
     * `null` when `expiry_date` is null.
     */
    days_until_expiry: z.number().int().nullable(),
});
export const dashboardComplianceSectionSchema = z.object({
    total_count: z.number().int().nonnegative(),
    rows: z.array(dashboardComplianceRowSchema),
    truncated: z.boolean(),
});
export const dashboardNqaItsRowSchema = z.object({
    person_id: idSchema,
    full_name: z.string().min(1),
    /** Latest `people.updated_at` — the trigger for the "needs sync"
     *  flag. ISO-8601 UTC. */
    last_changed_at: isoDateTimeSchema,
    /** Last time the admin marked the person as synced. Null when the
     *  person has never been synced. */
    nqa_its_synced_at: isoDateTimeSchema.nullable(),
});
export const dashboardNqaItsSectionSchema = z.object({
    total_count: z.number().int().nonnegative(),
    rows: z.array(dashboardNqaItsRowSchema),
    truncated: z.boolean(),
});
export const adminDashboardResponseSchema = z.object({
    /** Sydney-local "today" the snapshots were computed against. */
    run_date: isoDateSchema,
    new_this_week: dashboardComplianceSectionSchema,
    expiring_in_30: dashboardComplianceSectionSchema,
    expired_overdue: dashboardComplianceSectionSchema,
    /** Volunteers whose latest regulator-relevant change is newer than
     *  their `nqa_its_synced_at`. Replaces the earlier "tasks due/overdue"
     *  shape (the per-trigger task table was disproportionate to the
     *  obligation — see migration 0005). */
    nqa_its_needs_sync: dashboardNqaItsSectionSchema,
    /** Cron health (M9 + EventBridge trigger refactor): one row per
     *  scheduled job, plus the count of unretried mail failures and stuck
     *  audit packs. The dashboard uses this to render the cron-health
     *  card and the failure-mode badges. */
    worker_jobs: z.object({
        cron_health: z.array(cronHealthEntrySchema),
        unretried_mail_failures_count: z.number().int().nonnegative(),
        stuck_audit_packs_count: z.number().int().nonnegative(),
    }),
});
// ===========================================================================
// Volunteer self-edit on `/me` — replaces the retired `/intake/:token`
// flow. The volunteer signs in via the standard email-OTP path and the
// `/me` dashboard surfaces a "complete your details" form when they
// land in `onboarding` status. Fields that the admin pre-filled are
// rendered alongside the editable inputs.
// ===========================================================================
/** WWCC number format (NSW OCG): clearance `WWC########E`, application
 *  `APP########E`. */
export const WWCC_NUMBER_REGEX = /^(WWC|APP)\d{8}E$/;
export const meIntakeSubmitRequestSchema = z.object({
    first_name: z.string().min(1),
    middle_name: z.string().min(1).nullable(),
    last_name: z.string().min(1),
    preferred_name: z.string().min(1).nullable(),
    date_of_birth: isoDateSchema,
    mobile_phone: z.string().min(1).nullable(),
    role_tags: z.array(z.string().min(1)),
    direct_child_contact: z.boolean(),
    start_date: isoDateSchema,
});
export const meIntakeSubmitResponseSchema = z.object({
    person_id: idSchema,
    submitted_at: isoDateTimeSchema,
});
// ===========================================================================
// Health / readiness
// ===========================================================================
export const healthResponseSchema = z.object({
    status: z.literal('ok'),
    uptime_seconds: z.number().nonnegative(),
});
export const readyResponseSchema = z.object({
    status: z.enum(['ok', 'degraded']),
    checks: z.object({
        database: z.enum(['ok', 'fail']),
        object_storage: z.enum(['ok', 'fail']),
    }),
});
// ===========================================================================
// Audit envelope helpers — useful for the audit-context shape some
//                          endpoints accept (e.g. ip / user_agent capture).
// ===========================================================================
/**
 * Optional client-side audit context every request may carry. Captured
 * here for completeness — the API also reads them from request headers.
 */
export const auditContextSchema = z.object({
    client_ip: inetSchema.nullable(),
    user_agent: z.string().nullable(),
});
//# sourceMappingURL=api.js.map